import { useState, useMemo, useEffect, useRef, useCallback } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import {
  FileText,
  Wallet,
  Store,
  GitBranch,
  Plus,
  Send,
  Save,
  Eye,
  AlertTriangle,
  Package,
} from "lucide-react"
import {
  REGION_OPTIONS,
  URGENCY_OPTIONS,
  ORDER_FOR_OPTIONS,
  ORDER_FOR_TYPE,
  currentDate,
  currentYearMonth,
  orderForDateLabel,
  yearMonthOfOrderForDate,
  approvalModeLabel,
  formatQuorum,
  formatVND,
  proposalLineKey,
  referencePriceVendorId,
  referencePriceItems,
} from "./constants"
import OrderSubmitConfirmModal from "./components/orders/OrderSubmitConfirmModal"
import OrderBudgetAllocation from "./components/orders/OrderBudgetAllocation"
import OrderForDatePicker from "./components/orders/OrderForDatePicker"
import { ORDER_STATUS, canEditOrder, canOpenOrderEdit } from "./status"
import {
  useGetActiveBudgetCategoriesQuery,
  useGetRequesterBudgetQuery,
} from "shared/redux/endpoints/procurementV2"
import { useGetPaymentAssigneesQuery } from "shared/redux/endpoints/procurementDirectory"
import { Role, getRoleCode } from "constant/Role"
import {
  useGetGroupItemsQuery,
  useGetProcurementCentersQuery,
  useGetProcurementSuppliersQuery,
  useCreateProcurementSupplierMutation,
  useGetItemsQuery,
  useLazyGetProcurementSupplierItemPricesQuery,
} from "shared/redux/endpoints/procurementItems"
import { useCreateVendorProposalRequestMutation } from "shared/redux/endpoints/procurementPurchases"
import { useGetDepartmentsQuery, useGetMyDepartmentsQuery } from "shared/redux/endpoints/procurement"
import { departmentList } from "./components/payment/create/NonPoPaymentBuilder"
import {
  useGetOrderReviewQuery,
  useLazyGetOrderReviewQuery,
  usePreviewOrderApprovalQuery,
  usePreviewOrderBudgetQuery,
  useCreateOrderRequestMutation,
  useUpdateOrderRequestMutation,
  useSubmitOrderRequestMutation,
  useResubmitOrderRequestMutation,
  useUploadOrderDocumentMutation,
} from "shared/redux/endpoints/procurementApprovals"
import { showToastSuccess, showToastError, showToastWarning } from "utilities/toastUtil"
import {
  Page,
  Card,
  Button,
  Currency,
  ProgressBar,
  StatusBadge,
  Alert,
  Loading,
  Input,
  Textarea,
  Select,
  ApprovalRoute,
  ProcessStepper,
  budgetHealth,
  clampPct,
} from "./ui"
import OrderLineRow from "./components/OrderLineRow"
import { isValidMonthQuantity, spreadAmounts } from "./components/orders/monthAmounts"
import PaymentAssigneePicker, { isValidEmail } from "./components/PaymentAssigneePicker"
import DocumentUploader from "./components/approvals/DocumentUploader"
import VendorProposalsPanel from "./components/proposals/VendorProposalsPanel"
import {
  getApiErrorMessage,
  resolveSubmitOutcome,
} from "./orderSubmitFeedback"
import useDebouncedValue from "./hooks/useDebouncedValue"
import { RULES, validateFields, validateOrderLine, hasErrors } from "./fieldRules"

// "Goods received" is a milestone on the linked PO (delivery_status), not a
// real OR status. This form only ever renders the stepper for draft/
// info_requested orders (the only editable statuses — see canEditOrder), so
// this step is never actually reached here, but it still needs a key that
// can't collide with the real ORDER_STATUS.INFO_REQUESTED value used by the
// step above it (see OrderReviewContainer / OrderDetailContainer for the bug
// that reusing it caused).
const GOODS_RECEIVED_STEP_KEY = "goods_received"

const ORDER_STEPS = [
  { key: ORDER_STATUS.DRAFT, label: "Draft" },
  { key: ORDER_STATUS.INFO_REQUESTED, label: "Info requested" },
  { key: ORDER_STATUS.PENDING, label: "Waiting for approval" },
  { key: ORDER_STATUS.APPROVED, label: "Approved" },
  { key: GOODS_RECEIVED_STEP_KEY, label: "Goods received" },
  { key: ORDER_STATUS.CLOSED, label: "Closed" },
]

let lineSeq = 0
const emptyLine = (centerId = "") => ({
  key: `line-${(lineSeq += 1)}`,
  id: null,
  itemId: null,
  itemName: "",
  itemCode: "",
  uom: "",
  // FE-only: scopes this line's item picker (see OrderLineRow `groupItems`).
  groupItemId: "",
  centerId,
  type: "new",
  replacedAssetCode: "",
  quantity: 1,
  unitPrice: 0,
  // A2 — when on, staff of this line's center can see the order via the
  // "shared to my center" list.
  showToCenter: false,
  deliveryContactName: "",
  deliveryContactPhone: "",
  deliveryAddress: "",
  note: "",
  ticketLink: "",
  proofImage: null,
  // Off-catalog item support — see OrderLineRow's itemOptions/handleItemChange.
  isCustomItem: false,
  customItemName: "",
  offCatalogUnit: "",
  stockReading: null,
})

const lineFromApi = (it) => ({
        key: `line-${(lineSeq += 1)}`,
        id: it.id ?? null,
        itemId: it.itemId ?? null,
        itemName: it.itemName || it.description || "",
        groupItemId: it.itemGroupId ?? it.groupItemId ?? "",
        centerId: it.centerId || "",
        type: (it.requestType || "new").toLowerCase() === "replacement" ? "replacement" : "new",
        replacedAssetCode: it.replacedAssetCode || "",
        quantity: it.quantity ?? 1,
        unitPrice: it.unitPrice ?? 0,
        isApplyReferencePrice: it.isApplyReferencePrice,
        showToCenter: it.showToCenter ?? false,
        deliveryContactName: it.deliverContactName || "",
        deliveryContactPhone: it.deliverContactPhone || "",
        deliveryAddress: it.deliverAddress || "",
        note: it.note || "",
        ticketLink: it.ticketLink || "",
        proofImage: null,
        // Off-catalog is "no itemId, has an off-catalog name" — the GET
        // response uses the same offCatalogItemName field as the wire
        // create payload (see buildLinePayload).
        isCustomItem: !it.itemId && !!it.offCatalogItemName,
        customItemName: it.offCatalogItemName || "",
        offCatalogUnit: it.offCatalogUnit || "",
        stockReading: null,
        status: it.status || null,
      })

const yearMonthOfLine = (it) =>
  it.fiscalYear && it.fiscalMonth ? `${it.fiscalYear}-${String(it.fiscalMonth).padStart(2, "0")}` : ""

// A multiple-months order stores one OR line per item per month; the form shows
// one card per item, so regroup by item + center + type. The k-th match in a
// month joins the k-th card, so the same item listed twice stays two cards.
const groupMonthLines = (apiLines) => {
  const groups = []
  const seen = new Map()
  apiLines.forEach((it) => {
    const ym = yearMonthOfLine(it)
    const id = [it.itemId ?? `oc:${it.offCatalogItemName}`, it.centerId, it.requestType, it.replacedAssetCode].join("|")
    const nth = `${id}|${ym}`
    const k = seen.get(nth) || 0
    seen.set(nth, k + 1)
    let group = groups.find((g) => g.groupId === id && g.k === k)
    if (!group) {
      group = { groupId: id, k, line: { ...lineFromApi(it), monthLines: {} } }
      groups.push(group)
    }
    if (ym) group.line.monthLines[ym] = { id: it.id ?? null, quantity: it.quantity ?? 1, unitPrice: it.unitPrice ?? 0 }
  })
  return groups.map((g) => g.line)
}

const linesFromOrder = (order) => {
  if (!order.lines?.length) return [emptyLine()]
  return order.orderForType === ORDER_FOR_TYPE.MULTIPLE_MONTHS
    ? groupMonthLines(order.lines)
    : order.lines.map(lineFromApi)
}

// One form line → the OR lines it is sent as. Multiple months: one per month,
// reusing that month's saved line id; qty/price stay per month until the card's
// own qty/price is edited (updateLine then evens them out).
const expandLine = (line, months, isMultiMonth) => {
  if (!isMultiMonth) return [{ line, id: line.id, quantity: line.quantity, unitPrice: line.unitPrice }]
  const saved = line.monthLines || {}
  const hasSaved = Object.values(saved).some((m) => m.id != null)
  return months.map((ym, i) => {
    const m = saved[ym]
    const [y, mo] = ym.split("-").map(Number)
    return {
      line,
      ym,
      id: m?.id ?? (!hasSaved && i === 0 ? line.id : null),
      quantity: m?.quantity ?? line.quantity,
      unitPrice: m?.unitPrice ?? line.unitPrice,
      fiscalYear: y,
      fiscalMonth: mo,
    }
  })
}
const amountOf = (x) => (Number(x.quantity) || 0) * (Number(x.unitPrice) || 0)
const monthGroupKey = ({ line }) =>
  line.centerId && line.itemId && !line.isCustomItem ? `${line.centerId}|${line.itemId}` : null

// Responsive field grid — collapses to one column on mobile. `cols` pins a
// column count on wide screens (see pageRefined.css); without it the grid
// auto-fits to `min`.
const FieldGrid = ({ children, min = 220, cols }) => (
  <div
    className={`pc-fgrid${cols ? ` pc-fgrid--${cols}` : ""}`}
    style={{ "--pc-fgrid-min": `${min}px` }}
  >
    {children}
  </div>
)

// One order can now span several centers, each with its own budget — this
// runs its own query for one center's remaining budget against just that
// center's own share of the order (`amount`, not the whole order's total),
// and reports whether it's over budget back up to the parent (which needs to
// know across every center to require the over-budget note/ticket/document).
const CenterBudgetCheck = ({
  centerId,
  centerName,
  budgetCenterId,
  categoryId,
  fiscalYear,
  fiscalMonth,
  amount,
  onStatusChange,
  first,
}) => {
  const { data: budgetList, isFetching: budgetLoading, error: budgetError } =
    useGetRequesterBudgetQuery({
      fiscalYear,
      fiscalMonth,
      centerId: budgetCenterId || undefined,
      categoryId: categoryId || undefined,
    })
  // A 404 (real or soft) means "no budget for this category/center" — don't let
  // the RTK cache's previous rows stand in for it.
  const budgetNotFound = Boolean(budgetList?.notFound) || Boolean(budgetError?.notFound)
  const budget = useMemo(
    () =>
      budgetNotFound
        ? null
        : (budgetList?.rows || []).find((b) => b.category === categoryId) || null,
    [budgetList, budgetNotFound, categoryId]
  )
  // BE counts a month with no budget as remaining 0 (over budget), not a blocker.
  const noBudget = !budgetLoading && budget?.remaining == null
  const remaining = noBudget ? 0 : budget?.remaining ?? null
  const exceeds = remaining != null && amount > remaining
  const afterRemaining = remaining != null ? remaining - amount : null
  const health = budgetHealth(remaining, budget?.total)

  useEffect(() => {
    onStatusChange(centerId, { exceeds })
  }, [centerId, exceeds, onStatusChange])

  return (
    <div className={`pc-center-budget${first ? "" : " pc-center-budget--next"}`}>
      <div className="pc-center-budget-name">{centerName}</div>
      {budgetLoading ? (
        <Loading label="Checking budget…" />
      ) : (
        <div>
          {noBudget && (
            <Alert icon={<AlertTriangle aria-hidden />} title="No budget allocated yet">
              {`Nothing allocated for this category through ${String(fiscalMonth).padStart(2, "0")}/${fiscalYear} — counted as 0, so this order is over budget.`}
            </Alert>
          )}
          <FieldGrid min={180}>
            <div className="pc-stat">
              <div className="pc-stat-label">Remaining budget</div>
              <div className="pc-stat-value" style={{ color: health.color }}>
                <Currency value={remaining} align="left" />
              </div>
              {budget?.accumulatedThroughMonth != null && (
                <div className="pc-hint">Accumulated through month {budget.accumulatedThroughMonth}</div>
              )}
            </div>
            <div className="pc-stat">
              <div className="pc-stat-label">This center&apos;s lines</div>
              <div className="pc-stat-value">
                <Currency value={amount} align="left" />
              </div>
            </div>
            <div className="pc-stat">
              <div className="pc-stat-label">Remaining after</div>
              <div className="pc-stat-value" style={{ color: exceeds ? "var(--pc-danger)" : "var(--pc-ink)" }}>
                <Currency value={afterRemaining} align="left" />
              </div>
            </div>
          </FieldGrid>
          <div className="pc-budget-fit">
            <ProgressBar
              pct={remaining > 0 ? clampPct((amount / remaining) * 100) : 100}
              color={exceeds ? "var(--pc-danger)" : "var(--pc-accent)"}
            />
            <div className="pc-budget-fit-badge">
              {exceeds ? (
                <StatusBadge status="rejected" label="Exceeds remaining budget" dot={false} />
              ) : (
                <StatusBadge status="approved" label="Within budget" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const MULTI_MONTH_LINE_ERROR = "Multiple months chỉ nhận item catalog có giá nguyên tắc."
const isNotReferencePriceLine = (l) => l.isCustomItem || (l.itemId != null && l.isApplyReferencePrice !== true)

const lineFromBookInventory = (src) => ({
  ...emptyLine(src.centerId || ""),
  itemId: src.itemId ?? null,
  itemName: src.itemName || "",
  itemCode: src.itemCode || "",
  uom: src.uom || "",
  groupItemId: src.groupItemId || "",
  centerId: src.centerId || "",
  quantity: Number(src.quantity) > 0 ? Number(src.quantity) : 1,
  unitPrice: Number(src.unitPrice) >= 0 ? Number(src.unitPrice) : 0,
  note: src.note || "",
})

const OrderRequestFormContainer = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: routeId } = useParams()
  // Book Inventory hands the new-order form a prefill through route state.
  // It is not posted by itself — the existing save/submit path still creates
  // the order, so a second click here cannot file a duplicate.
  const bookPrefill =
    !routeId && location.state?.bookInventory?.source === "book-inventory" && location.state.bookInventory.lines?.length
      ? location.state.bookInventory
      : null
  // Default center for a brand-new order (the logged-in user's own center).
  // Once an order exists, its *own* centerId (which can diverge from the
  // creator's — e.g. edited directly, or the order later reassigned) is
  // tracked separately in `orderCenterId` below and takes over — see the
  // `existing` effect. Sending this raw auth value unconditionally on every
  // PUT was the bug: editing an existing order always silently reset its
  // centerId back to whatever the current user's center happens to be.
  const authCenterId = useSelector((state) => state.auth.centerId)
  // A6 — the centers this user may order for = the same list that backs the
  // left-menu role/center switcher (auth.centerIds). Empty = org-wide (no
  // scoping), e.g. procurement staff who aren't pinned to specific centers.
  const authCenterIds = useSelector((state) => state.auth.centerIds)
  const authUser = useSelector((state) => state.auth.user)
  // A6 — procurement staff order for any center; only center users are scoped
  // to their managed centers.
  const activeRoleCode = useSelector((state) => getRoleCode(state.auth?.role))
  const isProcurementStaff =
    activeRoleCode === Role.PROCUREMENT || activeRoleCode === Role.PROCUREMENT_MANAGER

  // Tracks whichever draft this form is currently attached to — seeded from
  // the URL (/order-requests/:id/edit) and set once a brand-new draft is
  // first created, so a second "Save Draft" click updates that same order
  // instead of creating another one.
  const [orderId, setOrderId] = useState(routeId || null)
  const isEdit = !!orderId

  const { data: existing, isFetching } = useGetOrderReviewQuery(orderId, { skip: !orderId })
  const [createOrder, { isLoading: creating }] = useCreateOrderRequestMutation()
  const [updateOrder, { isLoading: updating }] = useUpdateOrderRequestMutation()
  const [submitOrder, { isLoading: submittingNew }] = useSubmitOrderRequestMutation()
  const [resubmitOrder, { isLoading: resubmitting }] = useResubmitOrderRequestMutation()
  const [uploadDocument] = useUploadOrderDocumentMutation()
  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )

  const [form, setForm] = useState({
    title: bookPrefill?.title || "",
    category: bookPrefill?.category || "",
    region: bookPrefill?.region || "",
    departmentId: "",
    urgency: "normal",
    // A3 — who runs the payment for this order; the order creator fills it in.
    paymentAssigneeId: "",
    // Set instead of paymentAssigneeId when the payer isn't in the list.
    paymentAssigneeEmail: "",
    paymentAssigneeEmailMode: false,
    reason: bookPrefill?.reason || "",
    orderForType: ORDER_FOR_TYPE.ONE_MONTH,
    // "YYYY-MM", sorted; ONE_MONTH keeps exactly one.
    orderForDates: [currentDate()],
  })

  // Master "Group Items" list (GET /v1/group-items?categoryId=), re-scoped to
  // the selected Category. Each order line picks its own group from it (see
  // OrderLineRow `groupItems`), which shows that group's account/L-codes.
  const { data: groupItems } = useGetGroupItemsQuery({
    categoryId: form.category || undefined,
  })

  // Vendor Proposals' Supplier dropdown — GET /v1/suppliers?is_active=.
  // Not scoped to the order's Category (a requester may quote any active
  // supplier, not only ones already tagged with that category).
  const { data: suppliers } = useGetProcurementSuppliersQuery({ isActive: true })

  // A3 — "Người làm Payment" options. The current user is always offered (and
  // is the default on a brand-new order) so the creator can keep it on
  // themselves; the directory lookup supplies the rest.
  const { data: paymentAssignees } = useGetPaymentAssigneesQuery()
  const currentUserId = authUser?.userId ?? authUser?.id
  const paymentAssigneeOptions = useMemo(() => {
    const byId = new Map()
    if (currentUserId != null) {
      byId.set(String(currentUserId), {
        value: String(currentUserId),
        label: `${authUser?.name || authUser?.fullName || "Tôi"} (bạn)`,
        email: authUser?.email,
      })
    }
    ;(paymentAssignees || []).forEach((u) => {
      if (!byId.has(String(u.id))) byId.set(String(u.id), { value: String(u.id), label: u.name, email: u.email })
    })
    // A saved payer set by email may not be in the list; the detail only
    // carries the resolved id, so show that rather than an empty picker.
    const savedId = existing?.paymentAssigneeId
    if (savedId != null && !byId.has(String(savedId))) {
      byId.set(String(savedId), { value: String(savedId), label: `User #${savedId}` })
    }
    return Array.from(byId.values())
  }, [paymentAssignees, currentUserId, authUser, existing?.paymentAssigneeId])
  // Default a new order's payer to the current user once options are known.
  const appliedDefaultAssignee = useRef(false)
  useEffect(() => {
    if (isEdit || appliedDefaultAssignee.current) return
    if (currentUserId == null) return
    appliedDefaultAssignee.current = true
    setForm((p) => (p.paymentAssigneeId ? p : { ...p, paymentAssigneeId: String(currentUserId) }))
  }, [isEdit, currentUserId])

  // itemCode/uom for the Vendor Proposals table below — order lines only
  // store itemId/itemName/quantity (see emptyLine), not the catalog's own
  // code/unit, so look those two up here rather than persisting them onto
  // every line. Lines can sit in different Group Items, so this covers the
  // whole Category.
  const { data: catalogItems } = useGetItemsQuery({ categoryId: form.category || undefined, size: 10000 })
  const itemCatalogById = useMemo(() => {
    const map = new Map()
    ;(catalogItems || []).forEach((it) => map.set(String(it.id), it))
    return map
  }, [catalogItems])
  const [createSupplier, { isLoading: creatingSupplier }] = useCreateProcurementSupplierMutation()
  // A just-created supplier is only guaranteed to show up in `suppliers`
  // after the invalidated list query refetches — a network round trip after
  // the create response comes back. Tracked here and merged into
  // supplierOptions below so the dropdown offers (and pre-fills) it
  // immediately, not "eventually".
  const [createdSuppliers, setCreatedSuppliers] = useState([])

  const [lines, setLines] = useState(() =>
    bookPrefill ? bookPrefill.lines.map(lineFromBookInventory) : [emptyLine()]
  )
  // The order's own centerId — defaults to the creator's own center for a
  // brand-new draft, but re-synced from `existing.centerId` once an order is
  // loaded (see the `existing` effect), so an already-saved value never gets
  // silently clobbered by a later PUT.
  const [orderCenterId, setOrderCenterId] = useState(authCenterId)
  // Files picked before the order has an id yet — flushed to the real upload
  // API right after the first successful save (see ensureSaved below).
  const [pendingFiles, setPendingFiles] = useState([])
  const [showRoute, setShowRoute] = useState(false)

  useEffect(() => {
    if (existing) {
      setForm({
        title: existing.title || "",
        category: existing.budgetCategoryId || existing.category || "",
        region: existing.region || "",
        departmentId: existing.departmentId ? String(existing.departmentId) : "",
        urgency: (existing.urgency || "normal").toLowerCase(),
        paymentAssigneeId: existing.paymentAssigneeId ? String(existing.paymentAssigneeId) : "",
        paymentAssigneeEmail: "",
        paymentAssigneeEmailMode: false,
        reason: existing.reason || "",
        orderForType: existing.orderForType || ORDER_FOR_TYPE.ONE_MONTH,
        orderForDates: existing.orderForDates?.length ? existing.orderForDates.map((d) => d.slice(0, 10)) : [currentDate()],
      })
      setOrderCenterId(existing.centerId ?? authCenterId)
      setLines(linesFromOrder(existing))
    }
  }, [existing])

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }))

  // Changing Category re-scopes the Group Items list (GET /v1/group-items
  // now takes categoryId) — clear the selected group instead of leaving a
  // stale pick that belongs to the old category.
  // A new category brings a new set of group items — every line's pick is stale.
  const handleCategoryChange = (v) => {
    setForm((p) => ({ ...p, category: v ? Number(v) : "" }))
    setLines((prev) => prev.map((l) => ({ ...l, groupItemId: "" })))
  }

  const handleRegionChange = (v) => setField("region", v)

  // Each department exists once per region (SO/NO) under the same name, so
  // the region code is what tells the two apart.
  const { data: departmentsRaw } = useGetDepartmentsQuery()
  const departmentOptions = useMemo(
    () =>
      departmentList(departmentsRaw).map((d) => ({
        value: String(d.teamId),
        label: d.region ? `${d.teamName} (${d.region})` : d.teamName,
      })),
    [departmentsRaw]
  )

  // New orders default to the creator's own department for the chosen region,
  // re-picked on region change until the user chooses one by hand.
  const [departmentTouched, setDepartmentTouched] = useState(false)
  const { data: myDepartmentsRaw } = useGetMyDepartmentsQuery(
    { region: form.region || undefined },
    { skip: isEdit || departmentTouched }
  )
  const defaultDepartmentId = useMemo(() => {
    const d = departmentList(myDepartmentsRaw).find((x) => x.isDefault)
    return d ? String(d.teamId) : ""
  }, [myDepartmentsRaw])
  useEffect(() => {
    if (isEdit || departmentTouched || !defaultDepartmentId) return
    setForm((p) => (p.departmentId === defaultDepartmentId ? p : { ...p, departmentId: defaultDepartmentId }))
  }, [isEdit, departmentTouched, defaultDepartmentId])

  // GET /private/procurement/v1/centers?region= — active centers, filtered
  // through hub.center_region (same join item-center-availability uses).
  const { data: centersPage } = useGetProcurementCentersQuery({
    region: form.region || undefined,
    size: 200,
  })
  const centerOptions = useMemo(() => {
    // A6 — restrict to the centers this user manages (auth.centerIds) so a
    // center user can only order for their own center(s). Procurement staff
    // are exempt (order for any center); empty centerIds also = no scoping.
    // Compared as strings since centerIds and the API's centerId can differ
    // in type.
    const managed = (authCenterIds || []).map(String)
    const scoped = !isProcurementStaff && managed.length > 0
    const items = (centersPage?.items || []).filter(
      (c) => !scoped || managed.includes(String(c.centerId))
    )
    return items.map((c) => ({
      value: c.centerId,
      // Center names aren't unique across budget centers (e.g. two "Bau
      // Cat" rows on different sun codes) — the code disambiguates them.
      label: c.sunCenterCode ? `${c.centerName} (${c.sunCenterCode})` : c.centerName,
    }))
  }, [centersPage, authCenterIds, isProcurementStaff])

  // Default center for a new line: the requester's own center if it's in the
  // (region-filtered) list, else the org's catch-all center — matched by
  // name since there's no dedicated "is default" flag on this master data.
  const defaultCenterId = useMemo(() => {
    const items = centersPage?.items || []
    if (!items.length) return ""
    if (authCenterId && items.some((c) => c.centerId === authCenterId)) return authCenterId
    const fallback = items.find(
      (c) => c.centerName === "Head Office" || c.budgetCenterName === "Unassigned / Chung"
    )
    return fallback?.centerId || ""
  }, [centersPage, authCenterId])

  // Backfills the default center onto any still-empty line once it's known —
  // covers the very first line (created before centersPage has loaded) and
  // any added while it was still loading. Only for a brand-new order: an
  // edited order's lines already carry their own saved centerId.
  const appliedDefaultCenter = useRef(false)
  useEffect(() => {
    if (isEdit || !defaultCenterId || appliedDefaultCenter.current) return
    appliedDefaultCenter.current = true
    setLines((prev) => prev.map((l) => (l.centerId ? l : { ...l, centerId: defaultCenterId })))
  }, [isEdit, defaultCenterId])

  // Region is now filtered server-side, so a line's previously-picked center
  // can go stale the moment the region changes (or the fetched list updates
  // for any other reason) — prune it instead of leaving an invalid pick.
  // Skips the very first population (nothing to prune on initial load).
  const skipFirstCenterPrune = useRef(true)
  useEffect(() => {
    if (skipFirstCenterPrune.current) {
      skipFirstCenterPrune.current = false
      return
    }
    const validIds = new Set(centerOptions.map((c) => String(c.value)))
    setLines((prev) => {
      let changed = false
      const next = prev.map((l) => {
        if (l.centerId && !validIds.has(String(l.centerId))) {
          changed = true
          return { ...l, centerId: "", stockReading: null }
        }
        return l
      })
      if (changed) showToastError("One or more lines had a center that is no longer valid for the selected region, and were removed.")
      return next
    })
  }, [centerOptions])

  const updateLine = (idx, patch) =>
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== idx) return l
        const next = { ...l, ...patch }
        if (l.monthLines && ("quantity" in patch || "unitPrice" in patch)) {
          next.monthLines = Object.fromEntries(
            Object.entries(l.monthLines).map(([ym, m]) => [
              ym,
              { ...m, quantity: next.quantity, unitPrice: next.unitPrice },
            ])
          )
        }
        return next
      })
    )
  const updateMonthQuantity = (idx, ym, quantity) =>
    setLines((prev) =>
      prev.map((l, i) =>
        i === idx
          ? {
              ...l,
              monthLines: {
                ...l.monthLines,
                [ym]: { id: null, unitPrice: l.unitPrice, ...l.monthLines?.[ym], quantity },
              },
            }
          : l
      )
    )
  const addLine = () => setLines((prev) => [...prev, emptyLine(defaultCenterId)])
  const removeLine = (idx) => setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)))

  // Total Amount PR — SUM(quantity × unit price) across every line. No manual
  // entry: this is the number that drives the Budget Check and the payload.
  // The OR lines actually sent (multiple months: one per card per month); `idx` = card.
  const orderForMonths = useMemo(() => form.orderForDates.map(yearMonthOfOrderForDate), [form.orderForDates])
  const sentLines = useMemo(() => {
    const multi = form.orderForType === ORDER_FOR_TYPE.MULTIPLE_MONTHS
    const expanded = lines.flatMap((l, idx) =>
      expandLine(l, orderForMonths, multi).map((x) => ({ ...x, idx }))
    )
    return spreadAmounts(expanded, multi ? monthGroupKey : () => null, amountOf, (x) => x.ym)
  }, [lines, orderForMonths, form.orderForType])
  const linesTotal = useMemo(() => sentLines.reduce((sum, x) => sum + amountOf(x), 0), [sentLines])
  const hasPaymentAssignee = form.paymentAssigneeEmailMode
    ? isValidEmail(form.paymentAssigneeEmail)
    : !!form.paymentAssigneeId
  const linesValid =
    lines.length > 0 &&
    lines.every(
      (l) =>
        l.centerId &&
        // Item no longer has to come from the catalog — an off-catalog line
        // just needs a name typed in instead of a real itemId.
        (l.isCustomItem ? l.customItemName.trim() : l.itemId) &&
        Number(l.quantity) > 0 &&
        Number(l.unitPrice) >= 0 &&
        l.deliveryContactName?.trim() &&
        l.deliveryContactPhone?.trim() &&
        (l.type !== "replacement" || l.replacedAssetCode.trim())
    )

  // Errors stay hidden until the first save attempt, then track edits live.
  const [showFieldErrors, setShowFieldErrors] = useState(false)
  const isMultiMonth = form.orderForType === ORDER_FOR_TYPE.MULTIPLE_MONTHS
  const orderErrors = {
    ...validateFields(form, { title: RULES.orderRequest.title }),
    ...(isMultiMonth && orderForMonths.length < 2 && { orderForMonths: "Chọn ít nhất 2 tháng." }),
  }
  const lineErrors = lines.map((l) => {
    const errs = validateOrderLine(l)
    if (isMultiMonth && isNotReferencePriceLine(l)) errs.referencePrice = MULTI_MONTH_LINE_ERROR
    if (isMultiMonth && sentLines.some((x) => x.line === l && !isValidMonthQuantity(x.quantity))) {
      errs.monthQuantity = "Each month needs a quantity of 0 or more."
    }
    return errs
  })
  const handleOrderForTypeChange = (v) => {
    if (v === ORDER_FOR_TYPE.MULTIPLE_MONTHS) {
      const bad = lines.map((l, i) => (isNotReferencePriceLine(l) ? i + 1 : null)).filter(Boolean)
      if (bad.length) {
        showToastError(`${MULTI_MONTH_LINE_ERROR} Đổi item ở line ${bad.join(", ")} trước.`)
        return
      }
    }
    setForm((p) => ({
      ...p,
      orderForType: v,
      orderForDates: v === ORDER_FOR_TYPE.ONE_MONTH ? p.orderForDates.slice(-1) : p.orderForDates,
    }))
  }
  // One date per month: BE keys budget and lines by month, so a later pick replaces.
  const setOrderForDates = (dates) =>
    setField(
      "orderForDates",
      [...new Map(dates.filter(Boolean).map((d) => [yearMonthOfOrderForDate(d), d])).values()].sort()
    )
  const checkFields = () => {
    setShowFieldErrors(true)
    if (!hasErrors(orderErrors) && !lineErrors.some(hasErrors)) return true
    showToastError("Một số trường chưa hợp lệ — kiểm tra các ô báo đỏ.")
    return false
  }

  const supplierOptions = useMemo(() => {
    const byId = new Map((suppliers || []).map((s) => [s.id, s]))
    createdSuppliers.forEach((s) => {
      if (!byId.has(s.id)) byId.set(s.id, s)
    })
    return Array.from(byId.values()).map((s) => ({
      value: s.id,
      label: s.name,
      taxCode: s.taxCode,
      email: s.email,
    }))
  }, [suppliers, createdSuppliers])

  // `/centers` rows carry two distinct id spaces — `centerId` (hub.Center,
  // what order lines / stock / item-availability key off) and
  // `budgetCenterId` (pro.budget_category's own center grouping, a coarser
  // bucket — e.g. hub center "Tran Hung Dao" #41 rolls up into budget center
  // "Binh Duong" #4). The Budget Check query below filters by budget center,
  // so `orderCenterId` (a hub centerId) must be translated through this map
  // first — sending the hub id straight through as `budgetCenterId` would
  // filter against the wrong id space and silently return no/wrong budget.
  // Keyed by String(centerId) throughout — a native <select>'s onChange
  // always yields a string (see the region-prune effect above using the same
  // String() normalization), while `centersPage.items[].centerId` comes back
  // as a number from the API. Comparing/looking up without normalizing both
  // sides means a line whose center was actually picked via the dropdown
  // (string id) never matches a Map built from the numeric API ids — it
  // silently got no budgetCenterId, and its Budget Check query went out with
  // no center filter at all.
  const budgetCenterIdByCenterId = useMemo(() => {
    const map = new Map()
    ;(centersPage?.items || []).forEach((c) => {
      if (c.centerId != null) map.set(String(c.centerId), c.budgetCenterId)
    })
    return map
  }, [centersPage])
  // centerId -> centerName, for labeling each center's own Budget Check block.
  const centerNameByCenterId = useMemo(() => {
    const map = new Map()
    ;(centersPage?.items || []).forEach((c) => {
      if (c.centerId != null) map.set(String(c.centerId), c.centerName)
    })
    return map
  }, [centersPage])

  // Order lines can now span several centers — one Budget Check block per
  // distinct center actually in use, each checked against just that center's
  // own share of the order (not the whole order's total). Falls back to the
  // order-level default center only while no line has picked one yet (e.g.
  // before the default-backfill effect above has run for a brand-new line).
  const centerAmounts = useMemo(() => {
    const byCenter = new Map()
    const activeCenterIds = lines.some((l) => l.centerId)
      ? lines.filter((l) => l.centerId).map((l) => String(l.centerId))
      : [orderCenterId].filter(Boolean).map(String)
    sentLines.forEach((x) => {
      const l = x.line
      const centerId = l.centerId ? String(l.centerId) : activeCenterIds.length === 1 ? activeCenterIds[0] : null
      const hasItem = l.isCustomItem ? l.customItemName.trim() : l.itemId
      if (!centerId || !hasItem || !(Number(x.quantity) > 0)) return
      byCenter.set(centerId, (byCenter.get(centerId) || 0) + amountOf(x))
    })
    return Array.from(byCenter.entries()).map(([centerId, amount]) => ({
      centerId,
      budgetCenterId: budgetCenterIdByCenterId.get(centerId),
      centerName: centerNameByCenterId.get(centerId) || `Center #${centerId}`,
      amount,
    }))
  }, [lines, sentLines, orderCenterId, budgetCenterIdByCenterId, centerNameByCenterId])

  // Budget Check — live remaining budget per center/category so the
  // requester knows if there's headroom *before* submitting. Each center
  // above runs its own query (see <CenterBudgetCheck>) and reports back here
  // whether it's over budget. BE checks budget through the last Order-for month.
  const [budgetYear, budgetMonth] = (orderForMonths[orderForMonths.length - 1] || currentYearMonth())
    .split("-")
    .map(Number)
  const [centerBudgetStatus, setCenterBudgetStatus] = useState({})
  const handleCenterBudgetStatus = useCallback((centerId, status) => {
    setCenterBudgetStatus((prev) =>
      prev[centerId]?.exceeds === status.exceeds ? prev : { ...prev, [centerId]: status }
    )
  }, [])
  const overBudgetCenterIds = useMemo(
    () =>
      new Set(
        Object.entries(centerBudgetStatus)
          .filter(([, s]) => s.exceeds)
          .map(([centerId]) => String(centerId))
      ),
    [centerBudgetStatus]
  )
  const isLineOverBudget = (line) => overBudgetCenterIds.has(String(line.centerId))
  const overBudgetLineErrors = lines.map((l) => {
    if (!isLineOverBudget(l)) return {}
    const errs = {}
    if (!l.note?.trim()) errs.note = "Bắt buộc khi vượt ngân sách"
    if (!l.ticketLink?.trim()) errs.ticketLink = "Bắt buộc khi vượt ngân sách"
    return errs
  })
  const overBudgetMissingDocument =
    overBudgetCenterIds.size > 0 && !(existing?.documents?.length || pendingFiles.length)
  // Draft save stays lenient; only submit enforces the over-budget extras.
  const checkSubmitFields = () => {
    if (!checkFields()) return false
    if (overBudgetLineErrors.some(hasErrors)) {
      showToastError("Vượt ngân sách — cần nhập Note và Link to Ticket cho các line báo đỏ.")
      return false
    }
    if (overBudgetMissingDocument) {
      showToastError("Vượt ngân sách — cần đính kèm ít nhất 1 tài liệu.")
      return false
    }
    return true
  }

  // Quotes are per item across every month and center (BE reads proposedAmount as
  // the price of that total), so catalog lines group into one row with the summed
  // qty. An off-catalog line can only be quoted once saved (by its line id).
  const proposalLines = useMemo(() => {
    const rows = new Map()
    sentLines.forEach((x) => {
      const l = x.line
      const qty = Number(x.quantity) || 0
      if (l.isCustomItem) {
        if (!l.id || !l.customItemName?.trim()) return
        const key = proposalLineKey({ orderRequestLineId: l.id, offCatalog: true })
        rows.set(key, {
          key,
          itemId: null,
          orderRequestLineId: l.id,
          offCatalog: true,
          itemName: l.customItemName,
          quantity: l.quantity,
          uom: l.offCatalogUnit,
        })
        return
      }
      if (!l.itemId || !l.itemName) return
      const key = proposalLineKey({ itemId: l.itemId })
      const prev = rows.get(key)
      if (prev) {
        prev.quantity += qty
        return
      }
      const catalog = itemCatalogById.get(String(l.itemId))
      rows.set(key, {
        key,
        itemId: l.itemId,
        itemName: l.itemName,
        quantity: qty,
        itemCode: catalog?.itemCode,
        uom: catalog?.uom,
      })
    })
    return [...rows.values()]
  }, [sentLines, itemCatalogById])

  // Budget allocation preview — only lines with a center are sent (BE 400s on
  // the rest); `lineIndexes` in the reply point into this list.
  const previewLines = sentLines.filter((x) => x.line.centerId)
  const budgetPreviewLineInfo = previewLines.map((x) => ({
    cardNo: x.idx + 1,
    itemName: x.line.itemName || x.line.customItemName || `Line ${x.idx + 1}`,
    centerName: centerNameByCenterId.get(String(x.line.centerId)),
  }))
  const budgetPreviewKey = useDebouncedValue(
    form.category && previewLines.length && !orderErrors.orderForMonths
      ? JSON.stringify({
          budgetCategoryId: form.category,
          orderForType: form.orderForType,
          orderForDates: form.orderForDates.length ? form.orderForDates : undefined,
          lines: previewLines.map((x) => ({
            centerId: x.line.centerId,
            itemId: x.line.isCustomItem ? undefined : x.line.itemId ?? undefined,
            amount: x.amount,
            fiscalYear: x.fiscalYear,
            fiscalMonth: x.fiscalMonth,
          })),
        })
      : "",
    500
  )
  const budgetPreviewArgs = useMemo(() => (budgetPreviewKey ? JSON.parse(budgetPreviewKey) : null), [budgetPreviewKey])
  const {
    currentData: budgetPreview,
    isFetching: budgetPreviewLoading,
    error: budgetPreviewError,
  } = usePreviewOrderBudgetQuery(budgetPreviewArgs, { skip: !budgetPreviewArgs })

  // Approval Route Preview — resolved from the shared rule engine. Debounce
  // the amount so editing a line doesn't fire a request per keystroke; the
  // call only goes out once typing pauses.
  const debouncedTotal = useDebouncedValue(linesTotal, 500)
  const { data: preview } = usePreviewOrderApprovalQuery(
    {
      entity: "order_request",
      category: form.category,
      region: form.region,
      department: form.departmentId,
      amount: debouncedTotal,
    },
    { skip: !form.category }
  )
  const routeSteps = useMemo(
    () =>
      (preview?.steps || []).map((s) => ({
        ...s,
        // Real API sends `approverNames` (array); ApprovalRoute renders a
        // single `approverLabel` string.
        approverLabel: Array.isArray(s.approverNames)
          ? s.approverNames.join(", ")
          : s.approverLabel || s.approvers || "—",
        modeLabel: `${approvalModeLabel(s.mode)} · ${formatQuorum(s.quorum)}`,
      })),
    [preview]
  )

  const status = existing?.status || ORDER_STATUS.DRAFT
  const canOpenEdit = canOpenOrderEdit(existing)
  // Editing a non-draft order (override on a pending order) persists
  // via Save Draft (PUT); the Submit/Resubmit button only applies to the
  // requester-editable statuses, so it's hidden in the override case.
  const isOfficerOverrideEdit = isEdit && !canEditOrder(status) && canOpenEdit

  useEffect(() => {
    if (!isEdit || isFetching || !existing) return
    if (canOpenEdit) return
    navigate(`/procurement/order-requests/${orderId}`, { replace: true })
  }, [isEdit, isFetching, existing, orderId, navigate, canOpenEdit])

  const handleCreateSupplierForProposal = async (draft) => {
    try {
      const created = await createSupplier({
        name: draft.name.trim(),
        taxCode: draft.taxCode.trim(),
        email: draft.email.trim() || undefined,
        budgetCategoryId: form.category || undefined,
      }).unwrap()
      setCreatedSuppliers((prev) => [...prev, created])
      showToastSuccess("Supplier created.")
      return created
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't create this supplier. Please try again."))
      throw e
    }
  }

  const buildCreatePayload = () => ({
    title: form.title.trim(),
    centerId: orderCenterId || undefined,
    region: form.region || undefined,
    departmentId: form.departmentId ? Number(form.departmentId) : undefined,
    budgetCategoryId: form.category || undefined,
    totalAmount: linesTotal,
    urgency: form.urgency ? form.urgency.toUpperCase() : undefined,
    reason: form.reason || undefined,
    // Always sent: a PUT without orderForType clears it on the BE.
    orderForType: form.orderForType,
    orderForDates: form.orderForDates.length ? form.orderForDates : undefined,
    // A3 — person responsible for the payment of this order. BE takes exactly
    // one of the two: an id from the list, or an email it looks up.
    ...(form.paymentAssigneeEmailMode
      ? { paymentAssigneeEmail: form.paymentAssigneeEmail.trim() || undefined }
      : { paymentAssigneeId: form.paymentAssigneeId || undefined }),
    // PUT replaces the OR's whole line set with this list. A still-blank row
    // is left out (BE would reject it), but a saved line is always sent so
    // clearing its item surfaces BE's error instead of silently deleting it.
    lines: sentLines
      .filter((x) => x.id || x.line.itemId || (x.line.isCustomItem && x.line.customItemName?.trim()))
      .map((x) => ({
        id: x.id ?? undefined,
        ...buildLinePayload({ ...x.line, quantity: x.quantity, unitPrice: x.unitPrice }),
        fiscalYear: x.fiscalYear,
        fiscalMonth: x.fiscalMonth,
      })),
  })

  const buildLinePayload = (line) => ({
    itemId: line.isCustomItem ? undefined : line.itemId,
    // Off-catalog item support — field names confirmed by the
    // DirectSourceRequest spec (2026-09 barrier-removal doc), which reuses
    // this exact OrderRequestLineCreateRequest type: `offCatalogItemName`,
    // not `isCustomItem`/`customItemName` as this used to (unconfirmed)
    // guess. The line's own UI state field names are unchanged — only the
    // wire mapping here does.
    offCatalogItemName: line.isCustomItem ? line.customItemName?.trim() || undefined : undefined,
    offCatalogUnit: line.isCustomItem ? line.offCatalogUnit?.trim() || undefined : undefined,
    centerId: line.centerId,
    requestType: line.type ? line.type.toLowerCase() : undefined,
    replacedAssetCode: line.type === "replacement" ? line.replacedAssetCode || undefined : undefined,
    quantity: Number(line.quantity) || 0,
    unitPrice: Number(line.unitPrice) || 0,
    // A2 — BE field show_to_center.
    showToCenter: !!line.showToCenter,
    deliverContactName: line.deliveryContactName?.trim() || undefined,
    deliverContactPhone: line.deliveryContactPhone?.trim() || undefined,
    deliverAddress: line.deliveryAddress?.trim() || undefined,
    note: line.note || undefined,
    ticketLink: line.ticketLink?.trim() || undefined,
  })

  // Any files picked before the order existed can now actually be uploaded.
  // Best-effort per file — one failing doesn't block the others or the save
  // that triggered this flush.
  const flushPendingFiles = async (targetId) => {
    if (!pendingFiles.length) return
    const files = pendingFiles
    setPendingFiles([])
    for (const { file, docType } of files) {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("docType", docType)
      try {
        await uploadDocument({ id: targetId, formData }).unwrap()
      } catch (e) {
        showToastError(getApiErrorMessage(e, `Couldn't upload ${file.name}. Please try again.`))
      }
    }
  }

  // Create-or-update: first save of a brand-new form POSTs and remembers the
  // returned id (redirecting to the edit URL so a refresh doesn't lose it);
  // every save after that — including the one right before Submit — PUTs to
  // that same id instead of creating a duplicate draft.
  const ensureSaved = async () => {
    let targetId
    if (orderId) {
      const updated = await updateOrder({ id: orderId, ...buildCreatePayload() }).unwrap()
      targetId = updated?.id || updated?.header?.id || orderId
      // New lines need their ids before the refetch lands, or a quick second
      // save would send them id-less again and BE would add duplicates.
      if (updated?.lines) setLines(linesFromOrder(updated))
    } else {
      const created = await createOrder(buildCreatePayload()).unwrap()
      targetId = created?.id || created?.header?.id || null
      if (!targetId) throw new Error("Draft was created, but we couldn't find its ID.")
      setOrderId(targetId)
      navigate(`/procurement/order-requests/${targetId}/edit`, { replace: true })
    }
    await flushPendingFiles(targetId)
    return targetId
  }

  const [confirmOpen, setConfirmOpen] = useState(false)
  const proposalFlushRef = useRef(null)
  // Saving refetches the order, and while that runs the whole page is swapped
  // for <Loading/>; a still-open confirm popup came back afterwards, letting a
  // second Send run submit again (BE 409: not a draft).
  const sendingRef = useRef(false)

  // An order made only of contract-price items (isApplyReferencePrice) from a
  // single default vendor needs no RFQ: create that vendor's proposal with its
  // catalogue prices and the backend auto-selects it. Skipped once the order
  // has any proposal, so changing vendor stays a manual "Select" in the panel.
  // Reads the saved order (not form state) since only the BE line response
  // carries isApplyReferencePrice/defaultVendorId and real line ids.
  const [fetchOrder] = useLazyGetOrderReviewQuery()
  const [fetchItemPrices] = useLazyGetProcurementSupplierItemPricesQuery()
  const [createReferenceProposal] = useCreateVendorProposalRequestMutation()
  const autoSelectReferenceVendor = async (targetId) => {
    const order = await fetchOrder(targetId).unwrap()
    const orderLines = order?.lines || []
    if ((order?.vendorProposals || []).length) return
    const supplierId = referencePriceVendorId(orderLines)
    if (supplierId == null) return

    const prices = await fetchItemPrices({ supplierId, orderRequestId: targetId }, false)
      .unwrap()
      .catch(() => [])
    const items = referencePriceItems(orderLines, prices)
    const vendorName = orderLines[0].defaultVendorName || `#${supplierId}`
    if (!items) {
      showToastWarning(`${vendorName} chưa có giá nguyên tắc còn hạn cho mọi item — gửi RFQ hoặc nhập báo giá.`)
      return
    }
    const created = await createReferenceProposal({
      orderRequestId: targetId,
      supplierIds: [supplierId],
      title: "Báo giá theo giá nguyên tắc",
      terms: ["Giá nguyên tắc theo hợp đồng khung"],
      pinEnabled: false,
      expiredAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      items,
    }).unwrap()
    // The BE only auto-selects when every item has a real price at this vendor.
    if ((created || []).some((p) => p.isSelected)) {
      showToastSuccess(`Đã tự chọn nhà cung cấp mặc định (${vendorName}) theo giá nguyên tắc.`)
    } else {
      showToastSuccess(`Đã tạo báo giá theo giá nguyên tắc của ${vendorName} — chọn báo giá để áp dụng.`)
    }
  }

  const handleSaveDraft = async () => {
    if (!checkFields()) return
    try {
      const savedId = await ensureSaved()
      if ((await proposalFlushRef.current?.()) === false) return
      await autoSelectReferenceVendor(savedId).catch((e) =>
        showToastError(getApiErrorMessage(e, "Couldn't auto-select the contract-price vendor — add a vendor proposal manually."))
      )
      showToastSuccess("Draft saved.")
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't save this draft. Please try again."))
    }
  }

  const handleSubmit = async () => {
    if (sendingRef.current) return
    sendingRef.current = true
    try {
      const savedId = await ensureSaved()
      if ((await proposalFlushRef.current?.()) === false) return
      await autoSelectReferenceVendor(savedId)
      const submitResult = await submitOrder(savedId).unwrap()
      const outcome = resolveSubmitOutcome({ submitResult })

      if (outcome.kind === "success") showToastSuccess(outcome.message)
      else showToastError(outcome.message)
      if (submitResult?.budgetWarning) {
        showToastWarning(`Order vượt ngân sách ${formatVND(submitResult.shortfall)} — approver sẽ thấy cảnh báo này.`)
      }

      navigate(`/procurement/order-requests/${savedId}`)
    } catch (e) {
      // Over-budget 400 carries BE's list of missing line fields/documents — show it verbatim.
      showToastError(getApiErrorMessage(e, "Couldn't submit this order. Please try again."))
    } finally {
      sendingRef.current = false
    }
  }

  const handleResubmit = async () => {
    if (sendingRef.current) return
    sendingRef.current = true
    try {
      const savedId = await ensureSaved()
      if ((await proposalFlushRef.current?.()) === false) return
      const submitResult = await resubmitOrder({ id: savedId, note: "" }).unwrap()
      const outcome = resolveSubmitOutcome({ submitResult })

      if (outcome.kind === "success") showToastSuccess("Order resubmitted for approval.")
      else showToastError(outcome.message)
      if (submitResult?.budgetWarning) {
        showToastWarning(`Order vượt ngân sách ${formatVND(submitResult.shortfall)} — approver sẽ thấy cảnh báo này.`)
      }

      navigate(`/procurement/order-requests/${savedId}`)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't resubmit this order. Please try again."))
    } finally {
      sendingRef.current = false
    }
  }

  if (isEdit && isFetching) {
    return (
      <Page breadcrumb={["Procurement", "Order Request"]} title="Order Request">
        <Loading />
      </Page>
    )
  }

  if (isEdit && existing && !canOpenEdit) {
    return (
      <Page breadcrumb={["Procurement", "Order Request"]} title="Order Request">
        <Loading />
      </Page>
    )
  }

  const isInfoRequested = status === ORDER_STATUS.INFO_REQUESTED
  const busy = creating || updating || submittingNew || resubmitting
  const saveDraftDisabled = busy || !form.title.trim() || !form.category

  return (
    <Page
      className="pc-page--refined pc-page--form"
      breadcrumb={[
        "Procurement",
        { label: "Orders", to: "/procurement/order-requests" },
        isEdit ? "Edit" : "Create Order",
      ]}
      title={isEdit ? existing?.orCode || "Order Request" : "New Order Request"}
      subtitle="Provide the request details and vendor quotes, then submit for approval."
      actions={
        <>
          <Button
            variant="secondary"
            onClick={() => navigate("/procurement/order-requests")}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Save aria-hidden />}
            disabled={saveDraftDisabled}
            onClick={handleSaveDraft}
          >
            Save Draft
          </Button>
        </>
      }
    >
      {bookPrefill && (
        <div className="bi-note">
          Prefilled from Book Inventory for {bookPrefill.lines.length} book{bookPrefill.lines.length === 1 ? "" : "s"}.
          Nothing is saved until you submit this order. Review the center, quantity, and delivery details first.
        </div>
      )}

      {/* Progress stepper */}
      <Card className="pc-progress-card">
        <ProcessStepper steps={ORDER_STEPS} currentKey={status} />
      </Card>

      {/* Order Information */}
      <Card
        icon={<FileText aria-hidden />}
        title="Order Information"
        subtitle="What you need, for whom, and why. This information is shared with approvers."
      >
        <FieldGrid cols={3}>
          <Input
            label="Request Title"
            required
            placeholder="e.g. Laptop for IT team"
            value={form.title}
            error={showFieldErrors ? orderErrors.title : undefined}
            onChange={(e) => setField("title", e.target.value)}
          />
          <Select
            label="Region"
            required
            value={form.region}
            onChange={handleRegionChange}
            options={REGION_OPTIONS}
            hint="Every line's center must belong to this region."
          />
          <Select
            label="Department"
            value={form.departmentId}
            onChange={(v) => {
              setDepartmentTouched(true)
              setField("departmentId", v)
            }}
            options={departmentOptions}
            hint="Routes the order to this department's manager when a rule requires it."
          />
          <Select
            label="Category"
            required
            value={form.category}
            onChange={handleCategoryChange}
            options={categoryOptions}
            hint="Drives the budget check and approval route."
          />
          <Select
            label="Urgency"
            value={form.urgency}
            allowEmpty={false}
            onChange={(v) => setField("urgency", v)}
            options={URGENCY_OPTIONS}
          />
          <PaymentAssigneePicker
            options={paymentAssigneeOptions}
            assigneeId={form.paymentAssigneeId}
            email={form.paymentAssigneeEmail}
            emailMode={form.paymentAssigneeEmailMode}
            onChange={({ emailMode, ...patch }) =>
              setForm((p) => ({ ...p, ...patch, ...(emailMode !== undefined && { paymentAssigneeEmailMode: emailMode }) }))
            }
          />
          <Input
            label="Request Date"
            value={existing?.requestDate || new Date().toISOString().slice(0, 10)}
            disabled
          />
          {isEdit && existing?.groupOrderCode && (
            <Input
              label="Procurement Batch"
              value={existing.groupOrderCode}
              disabled
              hint="Assigned at creation — can't be changed afterward."
            />
          )}
        </FieldGrid>
        <div style={{ height: 16 }} />
        <FieldGrid cols={1}>
          <Textarea
            label="Reason for request"
            placeholder="Why is this purchase needed? (e.g. replacing broken laptops for the onboarding team)"
            hint="Give approvers the business justification."
            value={form.reason}
            rows={3}
            onChange={(e) => setField("reason", e.target.value)}
          />
        </FieldGrid>
        <div style={{ height: 16 }} />
        <div className="pc-label">Budget allocation info</div>
        <div className="pc-budget-ctx" style={{ alignItems: "flex-start" }}>
          <div className="pc-budget-ctx-select">
            <Select
              label="Order for"
              value={form.orderForType}
              onChange={handleOrderForTypeChange}
              options={ORDER_FOR_OPTIONS}
              allowEmpty={false}
            />
          </div>
          {isMultiMonth ? (
            <>
            <div className="pc-budget-ctx-select">
              <OrderForDatePicker
                label="Add date"
                value=""
                stayOpen
                markedDates={form.orderForDates}
                error={showFieldErrors ? orderErrors.orderForMonths : undefined}
                hint=""
                onChange={(date) => setOrderForDates([...form.orderForDates, date])}
              />
            </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, flexBasis: "100%" }}>
                {form.orderForDates.map((d) => (
                  <span key={d} className="pc-badge">
                    {orderForDateLabel(d)}
                    <button
                      type="button"
                      aria-label={`Remove ${orderForDateLabel(d)}`}
                      onClick={() => setOrderForDates(form.orderForDates.filter((x) => x !== d))}
                      style={{ marginLeft: 4, border: 0, background: "none", cursor: "pointer", padding: 0 }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div className="pc-budget-ctx-select">
              <OrderForDatePicker
                label="Date"
                value={form.orderForDates[0] || ""}
                onChange={(date) => setOrderForDates([date])}
              />
            </div>
          )}
        </div>
      </Card>

      {/* Order Lines */}
      <Card
        icon={<Package aria-hidden />}
        title="Order Lines"
        subtitle="One row per item being requested — quantity × unit price drives the total below."
        action={
          <Button variant="secondary" size="sm" leftIcon={<Plus aria-hidden />} onClick={addLine}>
            Add line
          </Button>
        }
      >
        <div className="pc-lines">
          {lines.map((l, idx) => (
            <OrderLineRow
              key={l.key}
              line={l}
              idx={idx}
              centerOptions={centerOptions}
              groupItems={groupItems || []}
              requireGroupItem
              referencePriceOnly={isMultiMonth}
              onMonthQuantity={updateMonthQuantity}
              monthBreakdown={
                isMultiMonth
                  ? sentLines
                      .filter((x) => x.idx === idx)
                      .map((x) => ({
                        key: x.ym,
                        label: `${x.ym.slice(5)}/${x.ym.slice(0, 4)}`,
                        quantity: x.quantity,
                        unitPrice: x.unitPrice,
                        amount: x.amount,
                      }))
                  : null
              }
              onUpdate={updateLine}
              onRemove={removeLine}
              canRemove={lines.length > 1}
              onStageProofImage={(file) =>
                setPendingFiles((p) => [...p, { file, docType: "image_proof" }])
              }
              overBudget={isLineOverBudget(l)}
              errors={showFieldErrors ? { ...overBudgetLineErrors[idx], ...lineErrors[idx] } : undefined}
            />
          ))}
        </div>
        <div className="pc-total-bar">
          <span className="pc-total-bar-label">Total Amount PR</span>
          <span className="pc-total-bar-value">
            <Currency value={linesTotal} strong />
          </span>
        </div>
      </Card>

      {/* Budget Check — one block per center actually used across the order
          lines below, since lines can now be split across several centers. */}
      <Card
        icon={<Wallet aria-hidden />}
        title="Budget Check"
        subtitle="What the system is checking before you can submit — live remaining budget per center for this category and month."
        tone="yellow"
      >
        <div className="pc-budget-ctx">
          <div className="pc-budget-ctx-chip">
            <span>Budget checked through</span>
            <strong>{`${String(budgetMonth).padStart(2, "0")}/${budgetYear}`}</strong>
          </div>
          {form.category && (
            <div className="pc-budget-ctx-chip">
              <span>Category</span>
              <strong>{categoryOptions.find((o) => o.value === form.category)?.label || "—"}</strong>
            </div>
          )}
        </div>
        {!form.category ? (
          <p className="pc-section-empty">
            Select a category above to see how much budget remains before you
            submit.
          </p>
        ) : centerAmounts.length === 0 ? (
          <p className="pc-section-empty">
            Add order lines with a center below to see how much budget remains before you submit.
          </p>
        ) : (
          <div className="pc-center-budgets">
            {centerAmounts.map((c, i) => (
              <CenterBudgetCheck
                key={c.centerId}
                centerId={c.centerId}
                centerName={c.centerName}
                budgetCenterId={c.budgetCenterId}
                categoryId={form.category}
                fiscalYear={budgetYear}
                fiscalMonth={budgetMonth}
                amount={c.amount}
                onStatusChange={handleCenterBudgetStatus}
                first={i === 0}
              />
            ))}
          </div>
        )}
      </Card>

      <OrderBudgetAllocation
        rows={budgetPreviewArgs ? budgetPreview?.rows || [] : []}
        lineInfo={budgetPreviewLineInfo}
        loading={budgetPreviewLoading}
        error={budgetPreviewArgs ? budgetPreviewError : null}
      />

      {/* Vendor Proposals */}
      <Card
        icon={<Store aria-hidden />}
        title="Vendor Proposals"
        subtitle="Attach and compare quotes from multiple vendors."
        tone="pink"
      >
        {lines.some((l) => l.isCustomItem || l.isApplyReferencePrice === false) && (
          <div style={{ marginBottom: 16 }}>
            <Alert tone="warn" icon={<AlertTriangle aria-hidden />}>
              This order includes an off-catalog or estimate-price item, so a vendor isn&apos;t required to
              submit — Procurement can request quotes and pick one after it&apos;s approved.
            </Alert>
          </div>
        )}
        <VendorProposalsPanel
          sourceType="order"
          sourceId={orderId}
          orderLines={proposalLines}
          supplierOptions={supplierOptions}
          onCreateSupplier={handleCreateSupplierForProposal}
          creatingSupplier={creatingSupplier}
          flushRef={proposalFlushRef}
        />
      </Card>

      {/* Attachments */}
      <DocumentUploader
        orderId={orderId}
        documents={existing?.documents}
        stagedFiles={pendingFiles}
        onStage={(file, docType) => setPendingFiles((p) => [...p, { file, docType }])}
        onRemoveStaged={(idx) => setPendingFiles((p) => p.filter((_, i) => i !== idx))}
      />

      {/* Approval Route Preview */}
      <Card
        icon={<GitBranch aria-hidden />}
        title="Approval Route Preview"
        subtitle="See who will approve this request before you submit."
        tone="purple"
        action={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Eye aria-hidden />}
            disabled={!form.category}
            onClick={() => setShowRoute((v) => !v)}
          >
            {showRoute ? "Hide route" : "Preview route"}
          </Button>
        }
      >
        {!form.category ? (
          <p className="pc-section-empty">
            Select a category to preview the approval route.
          </p>
        ) : !showRoute ? (
          <p className="pc-section-empty">
            Click “Preview route” to see the approvers for this category, region
            and amount.
          </p>
        ) : routeSteps.length ? (
          <div>
            {preview?.ruleName && (
              <p className="pc-hint" style={{ marginBottom: 12 }}>
                Matched rule: <strong>{preview.ruleName}</strong>
                {preview.version ? ` (v${preview.version})` : ""}
              </p>
            )}
            <ApprovalRoute steps={routeSteps} vertical />
          </div>
        ) : (
          <p className="pc-section-empty">
            No approval route resolved for this request.
          </p>
        )}
      </Card>

      {/* Action bar */}
      <div className="pc-action-bar">
        <Button
          variant="secondary"
          leftIcon={<Save aria-hidden />}
          disabled={saveDraftDisabled}
          onClick={handleSaveDraft}
        >
          Save Draft
        </Button>
        {!isOfficerOverrideEdit && (
          <Button
            variant="primary"
            leftIcon={<Send aria-hidden />}
            disabled={
              busy ||
              !form.title.trim() ||
              !form.category ||
              !form.region ||
              !hasPaymentAssignee ||
              !linesValid
            }
            onClick={() => checkSubmitFields() && setConfirmOpen(true)}
          >
            {isInfoRequested ? "Resubmit" : "Submit for Approval"}
          </Button>
        )}
      </div>
      {confirmOpen && (
        <OrderSubmitConfirmModal
          title={isInfoRequested ? "Resubmit" : "Submit for Approval"}
          rows={[
            { label: "Request title", value: form.title },
            { label: "Category", value: categoryOptions.find((o) => o.value === form.category)?.label },
            { label: "Region", value: form.region },
            { label: "Department", value: departmentOptions.find((o) => o.value === form.departmentId)?.label },
            { label: "Urgency", value: form.urgency },
            { label: "Lines", value: lines.length },
            { label: "Total amount", value: formatVND(linesTotal) },
            { label: "Reason for request", value: form.reason, full: true },
          ]}
          busy={busy}
          onCancel={() => !busy && setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false)
            return isInfoRequested ? handleResubmit() : handleSubmit()
          }}
        />
      )}
    </Page>
  )
}

export default OrderRequestFormContainer
